import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import classNames from 'classnames';
import { get } from 'lodash-es';
import moment from 'moment';
import { NumberCell } from '@/pages/dashboard/components/CommonTable/components/CommonTableCell/NumberCell';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import { ISparklineApiResult, ISparklineCellProps } from '@/pages/dashboard/components/CommonTable/components/CommonTableCell/SparklineCell/type';
import { formatPercentNum, getUpAndDownStatus } from '@/pages/dashboard/utils/utils';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import Loadings from '@/pages/dashboard/components/Loadings';
import './index.scss';

export const SparklineCell = (props: ISparklineCellProps) => {
  const { className, style, formattedValue, record, config } = props;
  const {
    changeKey = '',
    sparklineKey = 'sparkline',
    sparklineDatesKey,
    dodDecimals = 1,
    color = '#5086F3',
    height = 32,
    width = 120,
    column,
    fetchApi,
    tooltipSeriesName,
    tooltipDateFormat,
    trendLastDays,
    metricBubble,
  } = config ?? {};

  const { isEN } = useLocal();

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const [apiResult, setApiResult] = useState<ISparklineApiResult | null>(null);
  const [chartLoading, setChartLoading] = useState<boolean>(false);

  const dodRaw = changeKey ? get(record, changeKey) : undefined;
  const dodFormatted = dodRaw !== undefined && dodRaw !== null
    ? formatPercentNum(parseFloat(String(dodRaw)), dodDecimals, true)
    : '-';
  const dodStatus = getUpAndDownStatus(dodRaw);

  /** 当存在独立接口时使用接口数据，否则降级到宽表字段 */
  let sparklineData: number[] = fetchApi
    ? (apiResult?.series?.[0]?.data ?? [])
    : ((record?.[sparklineKey] as number[] | undefined) ?? []);

  let xAxisDates: string[] = fetchApi
    ? (apiResult?.dates ?? [])
    : (sparklineDatesKey && Array.isArray(record?.[sparklineDatesKey])
      ? (record[sparklineDatesKey] as string[])
      : sparklineData.map((_, i) => String(i)));

  if (trendLastDays && trendLastDays > 0 && sparklineData.length > trendLastDays) {
    sparklineData = sparklineData.slice(-trendLastDays);
    xAxisDates = xAxisDates.slice(-trendLastDays);
  }

  /** 独立接口拉取 */
  useEffect(() => {
    if (!fetchApi) return;
    setChartLoading(true);
    setApiResult(null);

    fetchApi(record)
      .then((res) => {
        setApiResult(res);
      })
      .catch((err: unknown) => {
        console.error('[SparklineCell] fetchApi error:', err);
      })
      .finally(() => {
        setChartLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  /** ECharts 初始化与更新 */
  useEffect(() => {
    if (!chartRef.current) return;
    if (chartLoading) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    /** 列名，用于 tooltip header */
    const columnName = isEN ? (column?.nameEn ?? column?.name ?? '') : (column?.nameCn ?? column?.name ?? '');
    /** 指标显示名 */
    const seriesLabel = tooltipSeriesName ?? columnName;

    const numericSparklineData = sparklineData.filter(v => Number.isFinite(v));
    const isSinglePoint = numericSparklineData.length === 1;
    // const yAxisMin = (() => {
    //   if (!numericSparklineData.length) {
    //     return undefined;
    //   }
    //   const minVal = Math.min(...numericSparklineData);
    //   // y 轴最小值下探 20%，保证低位趋势有可视空间
    //   if (minVal === 0) {
    //     return -0.2;
    //   }
    //   return minVal - Math.abs(minVal) * 0.2;
    // })();

    const option: echarts.EChartsOption = {
      animation: false,
      grid: { top: 4, right: 4, bottom: 4, left: 4 },
      xAxis: {
        type: 'category',
        show: false,
        boundaryGap: isSinglePoint,
        data: xAxisDates,
      },
      yAxis: {
        type: 'value',
        show: false,
        // min: yAxisMin,
      },
      tooltip: {
        trigger: 'axis',
        enterable: false,
        appendToBody: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        formatter: (params: Object|unknown[]) => {
          const paramList = Array.isArray(params) ? params : [params];
          if (!paramList.length) return '';
          const rawDate = String(paramList[0]?.name ?? '');
          const date = tooltipDateFormat ? moment(rawDate).format(tooltipDateFormat) : rawDate;
          const data = paramList.map((p) => {
            const rawVal = p.value;
            const metricsNum = dataCommonFormat({
              value: rawVal,
              formatRule: column?.features?.format,
            });
            return {
              name: seriesLabel,
              metricsNum,
              color: String(p.color ?? color),
            };
          });
          return getEchartTooltipText({
            title: columnName,
            metricsName: date,
            data,
            hasDot: true,
            hasRatio: false,
          }) ?? '';
        },
      },
      series: [
        {
          type: 'line',
          name: seriesLabel,
          data: sparklineData,
          smooth: true,
          symbol: isSinglePoint ? 'circle' : 'none',
          symbolSize: isSinglePoint ? 6 : 0,
          lineStyle: { color, width: 1.5 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${color}40` },
              { offset: 1, color: `${color}00` },
            ]),
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);

    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sparklineData, xAxisDates, color, chartLoading, isEN]);

  return (
    <NumberCell className={classNames('dbd-common-table__sparkline-cell', className)} style={style} config={{ column }}>
      <span className="dbd-common-table__sparkline-cell__inner">
        <span className="dbd-common-table__sparkline-cell__metric">
          {metricBubble ? metricBubble(
            <MetricAndUpDown
              metric={formattedValue}
              updown={dodRaw !== undefined ? { data: dodFormatted, status: dodStatus } : undefined}
              hasTooltip={false}
            />,
          ) : (
            <MetricAndUpDown
              metric={formattedValue}
              updown={dodRaw !== undefined ? { data: dodFormatted, status: dodStatus } : undefined}
              hasTooltip={false}
            />
          )}
        </span>
        {chartLoading && (
          <Loadings
            className="dbd-common-table__sparkline-cell__loading"
            style={{ width, height }}
            text=""
          />
        )}
        {!chartLoading && sparklineData.length > 0 && (
          <span
            className="dbd-common-table__sparkline-cell__chart"
            ref={chartRef}
            style={{ width, height }}
            title=""
          />
        )}
      </span>
    </NumberCell>
  );
};
